import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { HistoryInteraction } from '../../model/common/history-interaction.model';
import { Interaction } from '../../model/common/interaction.model';
import { SelectionMode } from '../../model/common/selection-mode.enum';
import { ProcessLandingConversionDto } from '../../model/interaction/process-landing-conversion.dto';
import { LandingConversion } from '../../model/landing-conversion/landing-conversion.model';
import { ClassifierMessage } from '../../model/mail-classifier/classifier-message.model';
import { LoadingStateService } from '../../services/base/loading-state.service';
import { SdApiService } from '../../services/base/sd-api.service';
import { ConfigurationService } from '../../services/configuration.service';
import { InteractionService } from '../../services/interaction.service';
import { LandingConversionsService } from '../../services/landing-conversions.service';
import { MailReviewService } from '../../services/mail-review.service';
import { SnackBarService } from '../../services/snackbar/snackbar.service';

@Component({
  selector: 'app-landing-conversion',
  templateUrl: './landing-conversion.component.html',
  styleUrl: './landing-conversion.component.scss',
  standalone: false,
})
export class LandingConversionComponent implements OnInit, OnDestroy {
  selectionMode: SelectionMode = SelectionMode.SINGLE;
  selectedConversion: LandingConversion = null;
  selectedInteraction: Interaction = null;
  selectedHistoryInteraction: HistoryInteraction = null;
  conversions: LandingConversion[] = [];
  pageSize = 5;
  isLoadingConversions = false;
  isLoadingEmail = false;
  columnHeaders = ['Customer Id', 'LinkedIn', 'First Name', 'Last Name', 'Company', 'Email', 'Comment'];
  displayedColumns = ['customerId', 'linkedInUrl', 'firstName', 'lastName', 'company', 'email', 'comment'];
  private destroy$ = new Subject<boolean>();

  constructor(
    private configurationService: ConfigurationService,
    private interactionService: InteractionService,
    private matDialog: MatDialog,
    private mailReviewService: MailReviewService,
    private landingConversionsService: LandingConversionsService,
    private loadingStateService: LoadingStateService,
    private sdApiService: SdApiService,
    private snackBarService: SnackBarService,
  ) {
    this.landingConversionsService.setLandingConversionContext$(true);
  }

  ngOnInit() {
    this.sdApiService.loadUserClientsSDRs();
    this.sdApiService.loadFolders();
    this.configurationService.loadCustomers();
    this.fetchConversions();
    this.selectedInteractionObs$();
    this.selectedConversionObs$();
    this.selectedHistoryInteractionObs$();
  }

  ngOnDestroy(): void {
    this.landingConversionsService.setLandingConversionContext$(false);
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  selectedHistoryInteractionObs$() {
    this.mailReviewService.selectedHistoryInteraction$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (historyInteraction) => {
        if (historyInteraction) {
          this.selectedHistoryInteraction = historyInteraction;
        }
      },
    });
  }

  selectedInteractionObs$() {
    this.mailReviewService.selectedInteraction$.pipe(takeUntil(this.destroy$)).subscribe((interaction) => {
      this.selectedInteraction = interaction;

      if (this.selectedInteraction) {
        this.getClientRepresentatives();
      } else {
        this.configurationService.setClientRepresentatives(null);
      }
    });
  }

  selectedConversionObs$() {
    this.landingConversionsService.selectedConversion$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (conversion) => {
        this.selectedConversion = conversion;
      },
    });
  }

  getClientRepresentatives() {
    this.configurationService.getClientRepresentatives(this.selectedInteraction.sdrId);
  }

  clearSearchValues() {
    this.mailReviewService.setClickedEmail('');
    this.mailReviewService.setClickedProspectId(null);
    this.mailReviewService.setSearchParams(null);
  }

  setClassifierMessage(classifierMessage: ClassifierMessage) {
    this.matDialog.closeAll();
    this.mailReviewService.setClassifierMessage(classifierMessage || null);
  }

  setEmailToGetInteractions(email: string) {
    this.mailReviewService.setClickedEmail(email);
  }

  fetchConversions() {
    this.isLoadingConversions = true;

    this.landingConversionsService
      .getLandingConversions()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoadingConversions = false)),
      )
      .subscribe({
        next: (response: LandingConversion[]) => {
          this.conversions = response || [];
        },
        error: () => {
          this.snackBarService.showError('An error occurred while fetching conversions');
          this.isLoadingConversions = false;
        },
      });
  }

  onSelectedChange(selected: any[]) {
    const selectedConversion: LandingConversion = selected[0];
    if (!selectedConversion) {
      this.landingConversionsService.setSelectedConversion$(null);
      return;
    }

    this.landingConversionsService.setSelectedConversion$(selectedConversion);
    this.clearSearchValues();
  }

  onRefresh() {
    this.mailReviewService.setSelectedInteraction(null);
    this.landingConversionsService.setSelectedConversion$(null);
    this.fetchConversions();
    this.clearSearchValues();
  }

  onPageChange() {
    this.landingConversionsService.setSelectedConversion$(null);
  }

  onProcessLandingConversion(processLandingConversionDto: ProcessLandingConversionDto) {
    this.loadingStateService.setLoadingState(true);

    this.interactionService
      .processLandingConversion(processLandingConversionDto)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loadingStateService.setLoadingState(false);
        }),
      )
      .subscribe({
        next: () => {
          this.snackBarService.showSuccess('The conversion has been successful');
          this.onRefresh();
        },
        error: () => {
          this.snackBarService.showError('An error occurred while processing the conversion.');
        },
      });
  }
}
